import { LoggerEnvironment } from '../core/config';
import { LogLevel } from '../core/levels';
import {
  getDefaultMetaFromRuntimeConfig,
  MinimalRuntimeConfig,
  Runtime,
  RuntimeConfigValidators,
  RuntimeLoggerConfigProvider,
} from '../core/runtimes/config';
import { JSONStreamConsoleFormatter, LocalConsoleFormatter } from './plugins/console';
import { NormalizeErrorMessage } from './plugins/error';
import { AppendLogLevel } from './plugins/level';
import { DefaultLogType } from './plugins/logType';
import { AppendNavigatorMeta } from './plugins/navigator';
import { NormalizeRequestLogEntry } from './plugins/request';
import { AppendTimestamp } from './plugins/timestamp';
import { AppendWindowMeta } from './plugins/window';
import { BeaconConfiguration, BeaconTransport, Beacon } from './plugins/beacon';
import { LogLevelConfig } from './logger';
import { Rollbar, RollbarConfiguration, RollbarTransport } from './plugins/rollbar';

const VERBOSE_TESTS = 'LOGGER_UNSILENCE_TESTS';

/**
 * BASE_PLUGINS is an array of plugins to be used with all runtimes
 */
const BASE_PLUGINS = [AppendLogLevel, AppendTimestamp, NormalizeErrorMessage, DefaultLogType, NormalizeRequestLogEntry];

/**
 * LogLevelRuntimeConfig defines the runtime configuration expected by the
 * loglevel logger.
 */
type LogLevelRuntimeConfig = MinimalRuntimeConfig & {
  beacon?: BeaconConfiguration;
  rollbar?: RollbarConfiguration;
};

/**
 * newFallbackConfig returns a console only logging configuration that should be used in case
 * of failed logger init
 */
const newFallbackConfig: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig> = (conf) => {
  return {
    level: LogLevel.ERROR,
    plugins: [JSONStreamConsoleFormatter, ...BASE_PLUGINS],
    defaultMeta: getDefaultMetaFromRuntimeConfig(Runtime.FALLBACK, conf),
    ...conf,
  };
};

/**
 * newTestConfig returns a console only logging configuration to be used with
 * tests
 */
const newTestConfig: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig> = (conf) => {
  // To enable debugging, we enable overriding the test level. This is to enable some
  // simple debugging if a test is failing as the test logger works otherwise.
  const level = process.env[VERBOSE_TESTS] ? LogLevel.TRACE : LogLevel.SILENT;

  return {
    level,
    plugins: [LocalConsoleFormatter({ level: 0 }), ...BASE_PLUGINS],
    defaultMeta: getDefaultMetaFromRuntimeConfig(Runtime.TEST, conf),
    ...conf,
  };
};

/**
 * newDevConfig returns a console only logging configuration to be used with
 * local development.
 *
 * FIXME: Consider just adding a test configuration instead of making the dev
 * configuration confusing.
 */
const newDevConfig: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig> = (conf) => {
  return {
    level: LogLevel.TRACE,
    plugins: [LocalConsoleFormatter({ level: undefined }), ...BASE_PLUGINS],
    defaultMeta: getDefaultMetaFromRuntimeConfig(Runtime.DEV, conf),
    ...conf,
  };
};

/**
 * newServerlessConfig is a logging configuration to be used with serverless
 * services like AWS Lambda or Fargate.
 */
const newServerlessConfig: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig> = (conf) => {
  const plugins = [JSONStreamConsoleFormatter];

  if (conf.rollbar) {
    plugins.push(Rollbar(new RollbarTransport({ ...conf.rollbar, level: LogLevel.ERROR })));
  }

  plugins.push(...BASE_PLUGINS);

  return {
    level: LogLevel.INFO,
    plugins: plugins,
    defaultMeta: getDefaultMetaFromRuntimeConfig(Runtime.SERVERLESS, conf),
    ...conf,
  };
};

/**
 * newBrowserConfig is a logging configuration to be used with a browser.
 */
const newBrowserConfig: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig> = (conf) => {
  const plugins = [];

  // FIXME: Beacon doesn't continue the plugin chain because we will otherwise emit
  // to the console.log...which we don't want. It must be the first plugin in the list.
  if (conf.beacon) {
    plugins.push(Beacon(new BeaconTransport(conf.beacon)));
  }

  if (conf.rollbar) {
    plugins.push(Rollbar(new RollbarTransport({ ...conf.rollbar, level: LogLevel.ERROR })));
  }

  plugins.push(...BASE_PLUGINS, AppendNavigatorMeta, AppendWindowMeta);

  return {
    level: LogLevel.INFO,
    plugins,
    defaultMeta: getDefaultMetaFromRuntimeConfig(Runtime.BROWSER, conf),
    ...conf,
  };
};

// Defines the RuntimeConfigProviders for development, serverless, and browser.
const LogLevelRuntimeConfigs: Record<Runtime, RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig>> = {
  fallback: newFallbackConfig,
  dev: newDevConfig,
  test: newTestConfig,
  serverless: newServerlessConfig,
  browser: newBrowserConfig,
};

/**
 * LogLevelRuntimeConfigValidators is an array of validators compatible with `RuntimeConfigValidators`.
 *
 * FIXME: Add validators for beacon and rollbar
 */
const LogLevelRuntimeConfigValidators: RuntimeConfigValidators<LogLevelRuntimeConfig> = [
  [
    'environment',
    [
      (conf) => (conf.environment ? null : 'environment is not defined'),
      (conf) =>
        Object.values(LoggerEnvironment).includes(conf.environment as LoggerEnvironment) ? null : 'invalid environment',
    ],
  ],
  ['service', [(conf) => (conf.service ? null : 'service is not defined')]],
  ['beacon', []],
  ['rollbar', []],
];

/**
 * LogLevelEnvToConfigMapper is an instance of EnvToConfigMap for the env variables expected
 * by the loglevel logger runtime.
 */
const LogLevelEnvToConfigMapper = {
  LOGGER_SERVICE: (val: string, conf: Partial<LogLevelRuntimeConfig>) => (conf.service = val),
  LOGGER_ENVIRONMENT: (val: string, conf: Partial<LogLevelRuntimeConfig>) =>
    (conf.environment = val as LoggerEnvironment),
  LOGGER_BEACON_ENDPOINT: (val: string, conf: Partial<LogLevelRuntimeConfig>) =>
    (conf.beacon = { ...(conf.beacon || {}), endpoint: val }),
  LOGGER_BEACON_DEBOUNCE: (val: string, conf: Partial<LogLevelRuntimeConfig>) =>
    (conf.beacon = {
      ...(conf.beacon || { endpoint: '' }),
      debounce: Number(val),
    }),
  LOGGER_BEACON_BATCH_LIMIT: (val: string, conf: Partial<LogLevelRuntimeConfig>) =>
    (conf.beacon = {
      ...(conf.beacon || { endpoint: '' }),
      batchLimit: Number(val),
    }),
  LOGGER_ROLLBAR_ACCESS_TOKEN: (val: string, conf: Partial<LogLevelRuntimeConfig>) =>
    (conf.rollbar = { ...(conf.rollbar || {}), accessToken: val }),
};

export type { LogLevelRuntimeConfig };
export { VERBOSE_TESTS, LogLevelRuntimeConfigs, LogLevelRuntimeConfigValidators, LogLevelEnvToConfigMapper };
